<template>

      <!-- label of section -->

      <div class="border-t-2 pt-2 mt-6 border-silver text-conversion" v-if="props.label">{{props.label}}:</div>

      <!-- text input -->

      <textarea class="w-full font-symbols text-lg mt-4 py-[6px] px-[14px] border-2 rounded-lg" :class="{ 'h-[44px]': props.maxRows && parseInt(props.maxRows) === 1, 'h-[72px]': !props.maxRows || parseInt(props.maxRows) > 1 }" :placeholder="$t('Sem napište požadovaný text')" v-model="tText" ref="tTextInput"></textarea>
      <div class="text-right text-sm">
        <template v-if="props.maxRows && parseInt(props.maxRows) > 1">
          Maximálně {{ props.maxRows }} {{ (props.maxRows > 4 ? 'řádků' : 'řádky') }} textu / {{ props.maxCharactersPerRow }} znaků na řádek
        </template>
        <template v-else-if="props.maxCharactersPerRow && parseInt(props.maxCharactersPerRow) > 1">
          Maximálně {{ props.maxCharactersPerRow }} znaků (zbývá <span class="inline-block min-w-[25px] text-left">{{ remainingCharacters }})</span>
        </template>
      </div>

      <!-- symbol keypad -->

      <div v-if="!isKeypadVisible" class="mt-4 mb-2 text-sm button" @click="isKeypadVisible=true">{{ $t('Vložit speciální symbol') }}</div>
      <MoleculesGlobalSymbolKeypad v-if="isKeypadVisible" @symbol-selected="appendSymbol($event, tTextInput)" />

      <!-- font selection -->

      <div class="mt-4 mb-2 text-sm">{{ $t('Výběr fontu') }}:</div>
      <div class="font-selection" ref="panFonts">
        <Multiselect :close-on-select="true" :canClear="false" :canDeselect="false" :options="fonts" label="name" valueProp="id" v-model="selectedFontId" class="z-[31]" @open="isFontDropdownOpen = true; showOverlay(panFonts)" @close="isFontDropdownOpen = false; hideOverlay">
          <template v-slot:singlelabel="{ value }">
            <div class="multiselect-single-label" style="flex-wrap: wrap; gap: 10px; justify-content: space-between; flex: 1;">
              <span style="width: 30%;">{{ value.name }}</span>

              <img
                v-if="tText"
                style="margin-left: auto; margin-right: auto;"
                :src="getFontPreviewUrl(value.id)"
              />
            </div>
          </template>

          <template v-slot:option="{ option }">
            <div style="display: flex; align-items: center; flex-wrap: wrap; gap: 10px; justify-content: space-between; flex: 1;">
              <span style="width: 30%;">{{ option.name }}</span>

              <img
                v-if="isFontDropdownOpen && tText"
                style="margin-left: auto; margin-right: auto;"
                :src="getFontPreviewUrl(option.id)"
              />
            </div>
          </template>
        </Multiselect>
      </div>
</template>
<script setup>

import { useBreakpoints } from '@vueuse/core';
import Multiselect from '@vueform/multiselect';
import "assets/css/multiselect.css";

const props = defineProps({
  selectedOption: Object,
  maxRows: Number,
  maxCharactersPerRow: Number,
  label: String
});


const utils = useUtils();
const appConfig = useAppConfig();
const view = useBreakpoints(appConfig.breakpoints);

const panFonts = ref(null);

const isOverlayVisible = ref(false);
const isKeypadVisible = ref(false);

const showOverlay = (scrollRef) => {
  if (view.smaller('desktop').value) {
    scrollTo(scrollRef.getBoundingClientRect().top + window.scrollY - 10, () => {
      isOverlayVisible.value = true;
      utils.setBodyScrollable(false);
    });
  }
}

const scrollTo = (top, onScrolledCallback) => {
  window.scrollTo({ top: top, behavior: 'smooth' });

  if (typeof onScrolledCallback === 'function') {
    setTimeout(() => onScrolledCallback(), 250);
  }
}

const hideOverlay = () => {
  if (view.smaller('desktop').value) {
    isOverlayVisible.value = false;
    utils.setBodyScrollable(true);
  }
}

const fonts = appConfig.serviceFonts.map((item) => {
  return {
    id: item,
    name: item.split('.')[0],
  }
}).sort((a, b) => a.name.startsWith('Quiche') ? -1 : a.name - b.name);

const selectedFontId = ref(fonts[0].id);

const tText = ref('');
const tTextInput = ref(null);

const remainingCharacters = computed(() => parseInt(props.maxCharactersPerRow) - tText.value.length);

const appendSymbol = (symbol, ref) => {
  const currentText = ref.value;
  const cursorPosition = ref.selectionStart;

  const newString = [currentText.slice(0, cursorPosition), symbol, currentText.slice(cursorPosition)].join('');

  ref.value = newString;
  tText.value = ref.value;
  ref.selectionStart = ref.selectionEnd = cursorPosition + 1;
  ref.focus();
};

const isValidSymbols = (text, symbols = []) => {
    const escapeRegex = s => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const extraSymbols = symbols.map(escapeRegex).join('');
    const regex = new RegExp(
        `^(?:[A-Za-z0-9Á-ž\\s.,;:!?'"()\\[\\]{}\\-_/\\\\@#%&+=*<>|~\`´^$€${extraSymbols}]|\\r?\\n)+$`,
        'u'
    );
    return regex.test(text);
}

const debouncedText = ref(tText.value);
let debounceTimer = undefined;

watch(tText, (value) => {
  clearTimeout(debounceTimer);

  debounceTimer = window.setTimeout(() => {
    debouncedText.value = value;
  }, 300);
});

const isFontDropdownOpen = ref(false);

const getFontPreviewUrl = (fontId) => {
  if (!debouncedText.value)
    return null;

  return appConfig.serviceTextRenderUrl
    .replace('{0}', encodeURIComponent(fontId))
    .replace('{1}', encodeURIComponent(debouncedText.value));
};

let serviceUpdatedTimer = null;

const serviceUpdatedDebounced = () => {
  clearTimeout(serviceUpdatedTimer);

  serviceUpdatedTimer = setTimeout(() => {
    serviceUpdated();
  }, 400);
};

let isSanitizing = false;

watch(tText, (newValue) => {
  if (isSanitizing) return;

  const original = newValue ?? '';

  const maxRows = parseInt(props.maxRows ?? '');
  const maxCharsPerRow = parseInt(props.maxCharactersPerRow ?? '');

  const singleLine = !props.maxRows || maxRows === 1;
  const multiLine = Number.isFinite(maxRows) && maxRows > 1;
  const hasCharLimit = Number.isFinite(maxCharsPerRow) && maxCharsPerRow > 0;

  let text = original.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

  let removedInvalidChars = false;
  let wasTrimmed = false;

  text = [...text].filter(ch => {
    if (ch === '\n') return true;

    const ok = isValidSymbols(ch, appConfig.keypadSymbols);
    if (!ok) removedInvalidChars = true;

    return ok;
  }).join('');

  if (singleLine) {
    if (text.includes('\n')) {
      wasTrimmed = true;
      text = text.replace(/\n+/g, ' ');
    }

    if (hasCharLimit && text.length > maxCharsPerRow) {
      wasTrimmed = true;
      text = text.substring(0, maxCharsPerRow);
    }
  }
  else if (multiLine) {
    let lines = text.split('\n');

    if (lines.length > maxRows) {
      wasTrimmed = true;
      lines = lines.slice(0, maxRows);
    }

    if (hasCharLimit) {
      lines = lines.map(line => {
        if (line.length > maxCharsPerRow) {
          wasTrimmed = true;
          return line.substring(0, maxCharsPerRow);
        }

        return line;
      });
    }

    text = lines.join('\n');
  }

  if (text !== original) {
    isSanitizing = true;
    tText.value = text;
    isSanitizing = false;

    if (!removedInvalidChars && wasTrimmed) {
      useMessage().show(
        "Text byl změněn, protože nesplnil požadavky na maximální počet řádků nebo znaků na řádek",
        null,
        false,
        true,
        'Ok',
        5
      );
    }
  }

  if (removedInvalidChars) {
    useMessage().show(
      "Povoleny jsou pouze obvyklé alfanumerické znaky latinské abecedy a symboly z nabídky",
      null,
      false,
      true,
      'Ok',
      5
    );
  }

  serviceUpdatedDebounced();
});

watch(selectedFontId, () => {
  serviceUpdatedDebounced();
});

const serviceUpdated = () => {

  let serviceConfiguration = null;

  if (selectedFontId.value && tText.value) {
    serviceConfiguration = {
      f: selectedFontId.value,
      t: tText.value,
      c: `https://www.sperky4u.eu/services.ashx?service=text&font=${encodeURIComponent(selectedFontId.value)}&text=${encodeURIComponent(tText.value)}&align=C&type=`
    };
  }

  emit('serviceChanged', serviceConfiguration);
};

const emit = defineEmits(['serviceChanged']);

</script>